"use client"
import React, { useEffect } from "react";
import MapLink from "@/components/shared/MapLink";
import LocationOpenHoursLink from "@/components/shared/LocationOpenHoursLink";
import UralityAvatarImage from "@/components/shared/UralityAvatarImage";
import PlaceTypeLabel from "@/components/shared/PlaceTypeLabel";
import { useConfig } from "@/lib/providers/ConfigProvider";
import CommunityAccoladeBadge from "@/components/communities/CommunityAccoladeBadge";
import AmenityViewList from "@/components/categories/AmenityViewList";
import useUtils from "@/components/shared/useUtils";
import { useCurrentLocation } from "@/lib/providers/GeolocationProvider";

import "@/components/maps/embed/EmbeddedContent.css";
import UralityImage from "@/components/shared/UralityImage";
import { Ratio } from "react-bootstrap";
import SocialLinks from "@/components/social/SocialLinks";

import "@/components/communities/CommunityDirectoryMemberView.css";
import DistanceView from "@/components/shared/DistanceView";
import RichTextBlock from "@/components/shared/RichTextBlock";

const CommunityDirectoryMemberView = ({
  id,
  className,
  wrapperClassName,
  community,
  profile,
  coordinates,
  size,
  external,
  onClick,
  showMap = true,
  waypoint,
  showCoverPhoto = false,
  sendToExternalWebsite = false,
  addTags,
  clickableLocation = false,
  enableDistanceTag = false,
  condensed = false
}) => {
  const { webUrl } = useConfig();
  const utils = useUtils();
  const { currentLocation } = useCurrentLocation();
  const url = () => {
    if (sendToExternalWebsite) {
      if (profile.location && profile.location.website) {
        return profile.location.website;
      } else {
        return "#";
      }
    } else {
      return community
        ? `${webUrl}${community.owner.resourceName}/community/members/${
            profile.resourceName
          }${waypoint ? `?waypt=${waypoint.id}` : ""}`
        : `${webUrl}${profile.resourceName}`;
    }
  };

  return (
    <div
      className={`community-directory-member-view ${className ?? ""}${wrapperClassName === "selected" ? " selected" : ""}`}
      id={id}
      key={id}
      onClick={onClick}
    >
      {showMap && <span className="anchor" id={"anchor-" + id} />}
      <div
        className={`${wrapperClassName ?? "default-card-wrapper"} ${
          onClick ? "clickable" : ""
        }`}
      >
        {showCoverPhoto && profile.coverPhotoId && (
          <Ratio aspectRatio="16x9">
            <UralityImage key={`${profile.id}-cover-photo`} imageId={profile.coverPhotoId} size="md" />
          </Ratio>
        )}
        <div className="embedded-content-section">
          <div className="header">
            <div className="avatar-title-and-text">
              <div className="avatar-and-title">
                {profile && profile.userProfileType && (
                  <UralityAvatarImage
                    key={`${profile.id}-logo`}
                    imageId={profile.logoId}
                    size={"sm"}
                    name={profile.displayName}
                    type={profile.userProfileType}
                  />
                )}
                <div className="text">
                  <h4>{profile.displayName}</h4>
                </div>
              </div>
              <div
                className={`place-type${
                  profile && profile.userProfileType ? " with-avatar" : ""
                }`}
              >
                {profile.userProfileType === "COMMUNITY" ? (
                  <span>Community</span>
                ) : (
                  <span>
                    <PlaceTypeLabel values={profile.types} />
                  </span>
                )}
              </div>
            </div>
            <div className="flex-grow" />
            <div className="accolades-and-tags">
              {profile.accolades && profile.accolades.length > 0 && (
                <div className="accolade-badge-list">
                  {profile.accolades.map(function(accolade, index) {
                    return (
                      <CommunityAccoladeBadge
                        key={`${profile.id}-accolade-badge-${index}`}
                        accolade={accolade}
                        enableHover={true}
                        tiny={true}
                      />
                    );
                  })}
                </div>
              )}
              <div className="flex-grow" />
              {addTags && addTags.length > 0 && (
                <div className="tags">
                  {addTags.map((tag, idx) => {
                    return <span key={`${profile.id}-tag-${idx}`} className="tag">{tag}</span>;
                  })}
                </div>
              )}
              {currentLocation &&
                !currentLocation.error &&
                profile.location &&
                enableDistanceTag && (
                  <div className="tags">
                    <span className="tag">
                      <DistanceView
                        key={`${profile.id}-distance-view`}
                        location={profile.location}
                        from={[
                          currentLocation.longitude,
                          currentLocation.latitude
                        ]}
                        to={[
                          profile.location.longitude,
                          profile.location.latitude
                        ]}
                        suffix="away"
                      />
                    </span>
                  </div>
                )}
            </div>
          </div>
        </div>
        {!condensed && (
          <>
          {profile.descriptionRichText && (
            <div className="embedded-content-section">
              <RichTextBlock
                key={`${profile.id}-description-rich-text`}
                richText={profile.descriptionRichText}
                collapsable={false}
                as="p"
              />
            </div>
          )}
          {profile.amenities && profile.amenities.length > 0 && (
          <div className="embedded-content-section">
            <AmenityViewList key={`${profile.id}-amenity-view-list`} profile={profile} condensed={true} />
          </div>
        )}

        {(profile.location || profile.publicEmailAddress) && (
          <div className="embedded-content-section vertical">
            {profile.userProfileType === "BUSINESS" &&
              profile.location?.hours && (
                <LocationOpenHoursLink
                  key={`${profile.id}-location-open-hours-link`}
                  location={profile.location}
                  showIcon={true}
                />
              )}
            {profile.location && (
              <MapLink
                key={`${profile.id}-map-link`}
                clickable={clickableLocation}
                location={profile.location}
              />
            )}
            {profile.location?.phoneNumber && (
              <a
                key={`${profile.id}-phone-number-link`}
                className="with-icon"
                href={"tel:" + profile.location.phoneNumber}
                onClick={evt => evt.stopPropagation()}
              >
                <span className="material-icons">call</span>
                {utils.formatPhoneNumber(profile.location.phoneNumber)}
              </a>
            )}
            {profile.publicEmailAddress && (
              <a href={`mailto:${profile.publicEmailAddress}`} className="with-icon">
                <span className="material-icons">mail</span>
                {profile.publicEmailAddress}
              </a>
            )}
          </div>
        )}

        {profile.socialProfile && profile.socialProfile.length > 0 && (
          <div className="embedded-content-section span">
            <SocialLinks key={`${profile.id}-social-links`} socialProfile={profile.socialProfile} />
          </div>
        )}
          </>
        )}
        

        
      </div>
    </div>
  );
};
export default CommunityDirectoryMemberView;
